/**
 * Interval Data Validation Utilities
 * WebView ve normal mod için interval verilerini validate etmek için kullanılır
 */

export interface IntervalData {
  start: string;
  end: string | null;
  duration: number | null;
}

export interface ValidationResult {
  isValid: boolean;
  cleanedData: string | null;
  errors: string[];
  warnings: string[];
  stats: {
    originalCount: number;
    validCount: number;
    totalDuration: number;
    averageDuration: number;
  };
}

/**
 * Validate and clean interval data
 */
export const validateAndCleanIntervalData = (intervals: string | undefined): ValidationResult => {
  const result: ValidationResult = {
    isValid: false,
    cleanedData: null,
    errors: [],
    warnings: [],
    stats: {
      originalCount: 0,
      validCount: 0,
      totalDuration: 0,
      averageDuration: 0
    }
  };

  // Handle empty or undefined intervals
  if (!intervals || intervals.trim() === '') {
    result.warnings.push('No interval data provided');
    return result;
  }

  try {
    // Parse JSON
    const parsed = JSON.parse(intervals);
    
    if (!Array.isArray(parsed)) {
      result.errors.push('Interval data must be an array');
      return result;
    }

    result.stats.originalCount = parsed.length;

    if (parsed.length === 0) {
      result.warnings.push('Empty interval array');
      return result;
    }

    // Validate and clean each interval
    const validIntervals: IntervalData[] = [];
    let totalDuration = 0;

    parsed.forEach((interval, index) => {
      const intervalErrors: string[] = [];
      
      // Check required fields
      if (!interval.start) {
        intervalErrors.push(`Interval ${index}: Missing start time`);
      }
      
      if (typeof interval.duration !== 'number') {
        intervalErrors.push(`Interval ${index}: Invalid duration type`);
      }
      
      // Validate duration
      if (typeof interval.duration === 'number') {
        if (interval.duration <= 0) {
          intervalErrors.push(`Interval ${index}: Duration must be positive`);
        } else if (interval.duration > 86400) { // 24 hours
          intervalErrors.push(`Interval ${index}: Duration too long (max 24 hours)`);
        } else if (interval.duration < 0.01) { // 10ms minimum
          intervalErrors.push(`Interval ${index}: Duration too short (min 10ms)`);
        }
      }
      
      // Validate start time format
      if (interval.start && typeof interval.start === 'string') {
        const startDate = new Date(interval.start.replace(' ', 'T'));
        if (isNaN(startDate.getTime())) {
          intervalErrors.push(`Interval ${index}: Invalid start time format`);
        }
      }
      
      // Validate end time format if provided
      if (interval.end && typeof interval.end === 'string') {
        const endDate = new Date(interval.end.replace(' ', 'T'));
        if (isNaN(endDate.getTime())) {
          intervalErrors.push(`Interval ${index}: Invalid end time format`);
        }
        
        // Check if end time is after start time
        if (interval.start) {
          const startDate = new Date(interval.start.replace(' ', 'T'));
          if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            if (endDate.getTime() <= startDate.getTime()) {
              intervalErrors.push(`Interval ${index}: End time must be after start time`);
            }
          }
        }
      }
      
      // If no errors, add to valid intervals
      if (intervalErrors.length === 0) {
        const cleanedInterval: IntervalData = {
          start: interval.start,
          end: interval.end || null,
          duration: Math.round(interval.duration * 100) / 100 // Round to 2 decimal places
        };
        
        validIntervals.push(cleanedInterval);
        totalDuration += cleanedInterval.duration || 0;
      } else {
        result.errors.push(...intervalErrors);
      }
    });

    // Update stats
    result.stats.validCount = validIntervals.length;
    result.stats.totalDuration = Math.round(totalDuration * 100) / 100;
    result.stats.averageDuration = validIntervals.length > 0 
      ? Math.round((totalDuration / validIntervals.length) * 100) / 100 
      : 0;

    // Check if we have any valid intervals
    if (validIntervals.length === 0) {
      result.errors.push('No valid intervals found');
      return result;
    }

    // Additional validations
    if (validIntervals.length < result.stats.originalCount) {
      result.warnings.push(`${result.stats.originalCount - validIntervals.length} invalid intervals were removed`);
    }

    // Check for suspicious patterns
    if (result.stats.averageDuration > 3600) { // 1 hour average
      result.warnings.push('Average session duration is very high (>1 hour)');
    }

    if (result.stats.averageDuration < 1) { // 1 second average
      result.warnings.push('Average session duration is very low (<1 second)');
    }

    // Sort intervals by start time
    validIntervals.sort((a, b) => {
      const dateA = new Date(a.start.replace(' ', 'T'));
      const dateB = new Date(b.start.replace(' ', 'T'));
      return dateA.getTime() - dateB.getTime();
    });

    // Check for overlapping intervals
    for (let i = 0; i < validIntervals.length - 1; i++) {
      const current = validIntervals[i];
      const next = validIntervals[i + 1];
      
      if (current.end) {
        const currentEnd = new Date(current.end.replace(' ', 'T'));
        const nextStart = new Date(next.start.replace(' ', 'T'));
        
        if (currentEnd.getTime() > nextStart.getTime()) {
          result.warnings.push(`Overlapping intervals detected between session ${i + 1} and ${i + 2}`);
        }
      }
    }

    // Success
    result.isValid = true;
    result.cleanedData = JSON.stringify(validIntervals);

  } catch (error) {
    result.errors.push(`JSON parsing error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  return result;
};

/**
 * Quick validation for interval data (less strict)
 */
export const quickValidateIntervals = (intervals: string | undefined): boolean => {
  if (!intervals) return false;
  
  try {
    const parsed = JSON.parse(intervals);
    return Array.isArray(parsed) && 
           parsed.length > 0 && 
           parsed.every(interval => 
             interval.start && 
             typeof interval.duration === 'number' && 
             interval.duration > 0
           );
  } catch {
    return false;
  }
};

/**
 * Extract total duration from interval data
 */
export const extractTotalDuration = (intervals: string | undefined): number => {
  if (!intervals) return 0;
  
  try {
    const parsed = JSON.parse(intervals);
    if (!Array.isArray(parsed)) return 0;
    
    return parsed.reduce((total, interval) => {
      return total + (typeof interval.duration === 'number' ? interval.duration : 0);
    }, 0);
  } catch {
    return 0;
  }
};

/**
 * Format interval data for display
 */
export const formatIntervalsForDisplay = (intervals: string | undefined): string[] => {
  if (!intervals) return [];
  
  try {
    const parsed = JSON.parse(intervals);
    if (!Array.isArray(parsed)) return [];
    
    return parsed.map((interval, index) => {
      const duration = interval.duration ? `${interval.duration}s` : 'Unknown';
      const start = interval.start ? new Date(interval.start.replace(' ', 'T')).toLocaleTimeString() : 'Unknown';
      const end = interval.end ? new Date(interval.end.replace(' ', 'T')).toLocaleTimeString() : 'Ongoing';
      
      return `Session ${index + 1}: ${start} - ${end} (${duration})`;
    });
  } catch {
    return ['Invalid interval data'];
  }
};